import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import { EdwardColors } from 'enums';
import { borderRadiusBox } from 'components/AppTheme/index';
import { ExtProductCategory } from 'pages/Accounts/Edit/components/ExternalProducts/model';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    count: {
      color: theme.palette.grey[400],
    },
    error: {
      color: theme.palette.error.main,
    },
    wrapper: {
      breakInside: 'avoid',
      pageBreakInside: 'avoid',
    },
    collapse: {
      margin: theme.spacing(-1),
      padding: theme.spacing(1),
      '&.MuiCollapse-hidden': { marginBottom: theme.spacing(2) },
    },
    moduleWrapper: {
      margin: theme.spacing(0, -1, 4, -1),
      width: `calc(100% + 16px)`,
      '& .MuiGrid-spacing-xs-1': { margin: 0 },
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(0, 0, 2, 0),
        width: '100%',
      },
    },
    dummy: {
      minHeight: theme.spacing(5),
      [theme.breakpoints.down('xs')]: {
        minHeight: 0,
      },
    },
    empty: { miHeight: 0, height: 0, margin: 0 },
    xsHide: {
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },
    smHide: {
      [theme.breakpoints.down('sm')]: { display: 'none' },
    },
    mw500: {
      '@media (max-width: 500px)': {
        flexGrow: 0,
        maxWidth: '100%',
        flexBasis: '100%',
      },
    },
    item: {
      borderRadius: '6px',
      boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
      background: theme.palette.common.white,
      margin: theme.spacing(1, 1),
      minHeight: '88px',
      breakInside: 'avoid',
      pageBreakInside: 'avoid',
      minWidth: 0,
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(1, 0),
      },
      overflow: 'hidden',
      position: 'relative',

      '&:before': {
        content: '""',
        display: 'inline-block',
        width: theme.spacing(1),
        height: '100%',
        background: 'transparent',
        position: 'absolute',
        left: 0,
        top: 0,
      },
    },
    target: { '&:before': { backgroundColor: EdwardColors.DARK } },
    reserve: { '&:before': { backgroundColor: EdwardColors.SECONDARY } },
    deposit: { '&:before': { backgroundColor: EdwardColors.GREEN } },
    inactive: { '&:before': { backgroundColor: EdwardColors.GRAY_MEDIUM } },
    depositMoney: {
      background: EdwardColors.GREEN_MEDIUM,
      color: '#fff',
      justifyContent: 'center',
      textTransform: 'uppercase',
      fontSize: '20px',
      '&:hover': {
        background: EdwardColors.GREEN_LIGHTER,
        cursor: 'pointer',
      },
      '& svg': {
        marginRight: theme.spacing(1),
      },
    },
    rentInfo: {
      paddingTop: '0',
    },
    rentTarget: {
      height: '100%',
      paddingTop: '0',
    },
    rentWrapper: {
      paddingBottom: theme.spacing(2),
      height: '100%',
    },
    graphDummy: {
      height: '80px',
    },
    moduleTitle: {
      padding: theme.spacing(0, 0, 0, 1),
      marginBottom: 0,
      minWidth: '156px',
    },
    sortIcon: {
      padding: 0,
      height: '36px',
      width: '36px',
      marginLeft: theme.spacing(2),
      '& svg': {
        height: '28px',
        width: '28px',
        color: '#000',
      },
    },
    orderIcon: {
      marginLeft: 0,
      '& svg': { transform: 'rotate(90deg)', transition: 'transform 0.3s cubic-bezier(0, 1, 0.5, 1)' },
      '&.false svg': { transform: 'rotate(-90deg)' },
    },
    title: {
      margin: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '20px',
      fontWeight: 800,
      '&.pdf': {
        fontSize: '11pt',
      },
      [theme.breakpoints.down('xs')]: {
        marginBottom: theme.spacing(0.5),
      },
      '&.rentTitle': {
        fontSize: '28px',
        fontWeight: 900,
        [theme.breakpoints.down('xs')]: {
          whiteSpace: 'wrap',
        },
      },
    },
    actions: {
      textAlign: 'right',
    },
    icon: {
      fontSize: '50px',
      textAlign: 'center',
      '& svg': {
        position: 'relative',
        top: theme.spacing(1),
        width: '1.2em',
        height: '1.2em',
        [theme.breakpoints.down('xs')]: {
          width: '0.8em',
          height: '0.8em',
          top: 0,
        },
      },
    },
    iconButton: {
      padding: 0,
      '& svg.MuiSvgIcon-fontSizeInherit': {
        fontSize: '50px',
        width: '1.2em',
        height: '1.2em',
      },
    },
    rentWithData: {
      paddingLeft: '32px',

      [theme.breakpoints.down('md')]: {
        paddingLeft: '4px',
      },
    },
    rentIcon: {
      fontSize: '80px',
      padding: `0 !important`,
      '&.overview': {
        fontSize: '72px',
      },
      [theme.breakpoints.up('xl')]: {
        maxWidth: '4.166666%',
        flexBasis: '4.166666%',
      },
      [theme.breakpoints.up('lg')]: {
        maxWidth: '8.33332%',
        flexBasis: '8.33332%',
      },
    },
    rentBuffer: {
      display: 'none',
      [theme.breakpoints.down('md')]: {
        display: 'flex',
      },
    },
    rentBuffer2: {
      display: 'none',
      maxWidth: '8.33332%',
      flexBasis: '8.33332%',
      '@media (min-width: 1280px) and (max-width: 1919px)': {
        display: 'flex',
      },
    },
    rentGraph: {
      paddingTop: '0 !important',
      paddingRight: '20px !important',
    },
    info: {
      '& > span, & div > span': {
        whiteSpace: 'nowrap',
        paddingRight: theme.spacing(1),
        borderLeft: 'solid #eee 4px',
        paddingLeft: theme.spacing(1),
        display: 'inline-flex',
      },
      [theme.breakpoints.down('xs')]: {
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
      },
      '& > .MuiGrid-item': {
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
      },
    },
    divider: {
      marginLeft: theme.spacing(1),
      borderLeft: 'solid #eee 4px',
      marginRight: theme.spacing(1),
    },
    grade: {
      position: 'absolute',
      top: 0,
      right: '6%',
      color: '#fff',
      height: '28px',
      borderBottomLeftRadius: '7px',
      borderBottomRightRadius: '7px',
      width: '180px',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '12px',
      fontWeight: 900,

      '& svg': {
        verticalAlign: 'middle',
        marginRight: theme.spacing(1),
        width: '1.2em',
        height: '1.2em',
      },

      '&:before, &:after': {
        content: '""',
        display: 'inline-block',
        position: 'absolute',
        borderBottomWidth: '12px',
        borderRightWidth: '12px',
        borderLeftWidth: '12px',
        borderTopWidth: '14px',
        borderColor: 'transparent',
        borderStyle: 'solid',
        top: 0,
        transform: 'scaleX(0.75)',
      },
      '&:before': {
        left: '-19px',
      },
      '&:after': {
        right: '-19px',
      },

      '&.grade_SHADOW': {
        background: theme.palette.grey['400'],
        filter: 'blur(4px)',
        '&:before': {
          borderRightColor: theme.palette.grey['400'],
          borderTopColor: theme.palette.grey['400'],
        },
        '&:after': {
          borderLeftColor: theme.palette.grey['400'],
          borderTopColor: theme.palette.grey['400'],
        },
      },

      '&.grade_NONE': {
        background: EdwardColors.GRAY_MEDIUM,
        '&:before': {
          borderRightColor: EdwardColors.GRAY_MEDIUM,
          borderTopColor: EdwardColors.GRAY_MEDIUM,
        },
        '&:after': {
          borderLeftColor: EdwardColors.GRAY_MEDIUM,
          borderTopColor: EdwardColors.GRAY_MEDIUM,
        },
      },
      '&.grade_PARTIAL': {
        background: theme.palette.primary.main,
        '&:before': {
          borderRightColor: theme.palette.primary.main,
          borderTopColor: theme.palette.primary.main,
        },
        '&:after': {
          borderLeftColor: theme.palette.primary.main,
          borderTopColor: theme.palette.primary.main,
        },
      },
      '&.grade_FULL': {
        background: EdwardColors.GREEN,
        '&:before': {
          borderRightColor: EdwardColors.GREEN,
          borderTopColor: EdwardColors.GREEN,
        },
        '&:after': {
          borderLeftColor: EdwardColors.GREEN,
          borderTopColor: EdwardColors.GREEN,
        },
      },
      '&.grade_IN_PROCESS': {
        background: EdwardColors.SECONDARY,
        '&:before': {
          borderRightColor: EdwardColors.SECONDARY,
          borderTopColor: EdwardColors.SECONDARY,
        },
        '&:after': {
          borderLeftColor: EdwardColors.SECONDARY,
          borderTopColor: EdwardColors.SECONDARY,
        },
      },

      '& > span': {
        display: 'inline-block',
        marginLeft: theme.spacing(2),
        '& > span': {
          backgroundColor: theme.palette.grey['600'],
          width: '14px',
          height: '14px',
          position: 'relative',
          transform: 'translate(6px, -1px)',
        },
        '& svg, & svg:hover': {
          margin: 0,
          color: '#fff',
          width: '18px',
          height: '18px',
          position: 'relative',
          left: '-2px',
          top: '-2px',
        },
      },
    },
    active: {
      fontSize: '16px',
      textAlign: 'right',
      [theme.breakpoints.down('xs')]: {
        textAlign: 'left',
      },
      '& strong': {
        display: 'block',
        width: '100%',
        color: theme.palette.common.black,
        fontSize: '12px',
        lineHeight: '32px',
        textAlign: 'right',
      },
    },
    showLink: {
      color: theme.palette.common.black,
      cursor: 'pointer',
      display: 'inline-block',
      position: 'relative',
      paddingRight: '3em',
      '&:hover': { textDecoration: 'none' },
    },
    showIcon: {
      fontSize: '3em',
      transition: `.2s all`,
      position: 'absolute',
      right: 0,
      top: '-40%',
    },
    shownIcon: {
      transform: 'rotate(-180deg)',
    },
    detailLink: {
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      color: theme.palette.common.black,
      borderTop: 'solid #ccc 2px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'flex-end',
      width: '100%',

      '&:hover': { textDecoration: 'none' },
    },
    detailControl: {
      display: 'inline-flex',
      alignItems: 'center',
      color: theme.palette.grey[600],
      '& svg': {
        transition: `.2s all`,
        marginLeft: theme.spacing(1),
      },
    },
    bankAccount: {
      minHeight: 'auto',
      margin: theme.spacing(1, 0),
      boxShadow: 'none',
      border: 'solid #ccc 1px',
    },
    primary: {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
    addBankAccount: {
      minHeight: 'auto',
      border: 'none',
      boxShadow: 'none',
      '&.MuiGrid-item': {
        marginLeft: 0,
        paddingLeft: 0,
      },
    },
    helperText: {
      opacity: 0.8,
    },
    formError: {
      margin: 0,
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(1),
      color: theme.palette.error.main,
    },
    compositionWrapper: {
      flexWrap: 'nowrap',
      '&.nominal': {
        flexWrap: 'wrap',
        '& > div': { maxWidth: '25%' },
      },
      [theme.breakpoints.down('sm')]: {
        flexWrap: 'wrap',
        maxWidth: 'none',
        '& > .MuiGrid-item': {
          flexGrow: 1,
        },
      },
    },
  }),
);

interface Props {
  plan?: boolean;
  detail?: boolean;
  pdf?: boolean;
  category?: ExtProductCategory;
}

export const useExtProdStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {
      display: 'flex',
      margin: theme.spacing(0, -1),
      flexWrap: 'wrap',
    },
    card: (props: Props) => ({
      transition: '0.5s all',
      //transform: props.pdf ? 'scale(0.667)' : undefined,
      //pageBreakInside: props.pdf ? 'avoid' : undefined,
      display: 'flex',
      flexWrap: 'wrap',
      borderRadius: borderRadiusBox,
      boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
      padding: props.pdf ? theme.spacing(2, 1, 1, 1) : theme.spacing(2),
      position: 'relative',
      overflow: 'hidden',
      margin: theme.spacing(1),
      background: theme.palette.common.white,

      minWidth: props.plan ? `calc(25% - 16px)` : `calc(50% - 16px)`,
      maxWidth: props.plan ? `calc(25% - 16px)` : `calc(50% - 16px)`,

      '@media (max-width: 1600px)': {
        minWidth: props.detail ? `calc(33% - 16px)` : props.plan ? `calc(25% - 16px)` : `calc(50% - 16px)`,
        maxWidth: props.detail ? `calc(33% - 16px)` : props.plan ? `calc(25% - 16px)` : `calc(50% - 16px)`,
      },
      [theme.breakpoints.down('md')]: {
        minWidth: props.plan ? 'calc(33.33% - 16px)' : 'calc(50% - 16px)',
        maxWidth: props.plan ? 'calc(33.33% - 16px)' : 'calc(50% - 16px)',
      },
      [theme.breakpoints.down('sm')]: {
        minWidth: 'calc(50% - 16px)',
        maxWidth: 'calc(50% - 16px)',
      },
      [theme.breakpoints.down('xs')]: {
        height: /*'470px'*/ 'auto',
        padding: theme.spacing(1),
        minWidth: 'calc(100% - 16px)',
        width: 'calc(100% - 16px)',
        maxWidth: 'calc(100% - 16px)',
        '& .MuiIconButton-root': { padding: '8px' },
      },

      '& > div': {
        width: '100%',
      },
      '& > span:first-child': {
        display: 'inline-block',
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '10px',
        background: '#000',
      },

      '& h3': {
        fontSize: '20px',
        fontWeight: 800,
        color: '#000',
        lineHeight: '1.2em',
        marginBottom: 0,
      },
      '& a': {
        display: 'block',
        width: '100%',
        paddingRight: '0',
      },

      height: props.category === ExtProductCategory.LOAN ? '330px' : '492px',
      '& > div:nth-child(2)': {
        height: '324px',
        [theme.breakpoints.down('xs')]: {
          height: 'auto',
        },
      },
      '&.collapsed': {
        height:
          props.category === ExtProductCategory.PROPERTY
            ? '230px'
            : props.category === ExtProductCategory.LOAN
            ? '218px'
            : '224px',
        '& > div:nth-child(2)': {
          height: '100%',
          overflow: 'hidden',
        },
      },

      '&._PDF': {
        pageBreakInside: 'avoid',
        boxShadow: 'none',
        border: 'solid #ccc 1px',
        fontSize: '66%',
        minWidth: `calc(33% - 16px)`,
        maxWidth: `calc(33% - 16px)`,
        height: '368px',
        '& > div:nth-child(2)': {
          height: '236px',
        },
        '& h3': {
          fontSize: '12pt',
          fontWeight: 500,
        },
        '& h4': {
          fontSize: '8pt',
          fontWeight: 800,
        },
      },
    }),
    balance: {
      fontSize: '20px',
      fontWeight: 800,
    },

    line: (props: Props) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: 'solid #eee 1px',
      //minHeight: theme.spacing(4),
      padding: theme.spacing(props.pdf ? '6px' : 1, 0, 0, 0),
      '&:first-child': {
        borderBottom: 'none',
      },
      '& span': {
        display: 'inline-flex',
      },
      '& span:first-child': {},
      '& span:last-child:not(:only-child)': {
        position: 'relative',
        justifySelf: 'end',
        fontWeight: 'bold',
        color: props.pdf ? theme.palette.common.black : undefined,
        textAlign: 'right',
        '&._actions': {
          position: 'absolute',
          right: 0,
        },
      },

      '&._PROPERTY': { paddingTop: '4px', '& svg': { top: 0 } },
      '&._LOAN': { paddingTop: '4px', '& svg': { top: 0 } },
    }),
    title: {
      width: '100%',
      paddingRight: '46px',
      textAlign: 'left',
      minHeight: '50px',
      '& svg': {
        top: '-5%',
      },
    },
    titlePdf: {
      width: '100%',
      textAlign: 'left',
      minHeight: '50px',
    },
    value: {
      '& span:last-child': {},
    },
    dummyLine: {
      padding: theme.spacing(1, 0, 0, 0),
      minHeight: '29px',
    },
    usage: {
      marginTop: theme.spacing(1),
      alignSelf: 'end',
    },
  }),
);

export default useStyles;
